import { Box, Typography, useTheme, styled, Dialog } from '@mui/material'
import { Button } from 'components'
import GenericButton from 'components/Button/GenericButton'
import ItemWrapper from 'components/ItemWrapper'
import RequiredFieldErrorMessage from 'components/ItemWrapper/RequiredFieldErrorMessage'
import FileUpload from 'components/FileUpload'
import InfoBoxWithIcon from 'components/InfoBoxWithIcon'
import { Icon } from 'packages/eid-icons'
import { Select, TextInput, useNotification } from 'packages/eid-ui'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EidAutocomplete } from 'packages/eid-controls'
import { useCSVInboxCategories, useCSVInboxTypes } from 'hooks'

export const CloseIconeBox = styled(Box)({
    '& svg': {
        position: 'absolute',
        top: '-10px',
        right: '-15px',
        cursor: 'pointer',
    },
})

export const CustomDialog = styled(Dialog)({
    zIndex: 1200,
    '& .MuiDialog-paperWidthSm': {
        width: '100%',
        '& .MuiBox-root': {
            // width: '100%',
        },
    },
    '& .MuiBox-root': {
        border: 'none',
        position: 'relative',
    },
})

// Initial form state
const initialFormState = {
    name: '',
    inboxType: null,
    ticketNo: '',
    comments: '',
    file: null,
}

// Initial error state
const initialErrorState = {
    name: '',
    inboxType: '',
    file: '',
}

const MassUpload = ({
    btnLabelKey,
    headerLabelKey,
    inboxTypes = [],
    onUpload,
    onValidate,
    isLoading = false,
}) => {
    const [openModal, setOpenModal] = useState(false)
    const [formData, setFormData] = useState(initialFormState)
    const [errors, setErrors] = useState(initialErrorState)
    const [apiErrors, setApiErrors] = useState([])
    const { data: inboxTypesData, isLoading: isLoadingInboxTypes } =
        useCSVInboxTypes(openModal) // enabled: true
    const { data: categoriesData, isLoading: isLoadingCategories } =
        useCSVInboxCategories(openModal) // enabled: true
    const { t } = useTranslation()
    const theme = useTheme()
    const { showSuccessMessage } = useNotification()

    // Generic field change handler
    const handleFieldChange = (field, value) => {
        setFormData((prev) => {
            const updatedData = {
                ...prev,
                [field]: value,
            }

            // Auto-populate name field with file name when file is selected
            if (field === 'file' && value) {
                // Extract file name without extension
                const fileName = value.name || ''
                const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '')

                // Only auto-populate if name is empty (allows user to edit later)
                if (!prev.name || prev.name.trim() === '') {
                    updatedData.name = nameWithoutExtension
                }
            }

            return updatedData
        })

        // Clear error when user starts typing/selecting
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }))
        }

        // Clear API errors when user makes changes
        if (apiErrors.length > 0) {
            setApiErrors([])
        }
    }

    // Validate form
    const validateForm = () => {
        const newErrors = { ...initialErrorState }
        let isValid = true

        // Name is required
        if (!formData.name.trim()) {
            newErrors.name = 'NameRequired'
            isValid = false
        }

        // Inbox Type is required
        if (!formData.inboxType) {
            newErrors.inboxType = 'InboxTypeRequired'
            isValid = false
        }

        // File is required
        if (!formData.file) {
            newErrors.file = 'FileRequired'
            isValid = false
        }
        // Resource System is required
        // if (!formData.resourceSystem) {
        //     newErrors.resourceSystem = 'ResourceSystemRequired'
        //     isValid = false
        // }

        setErrors(newErrors)
        return isValid
    }

    // const handleUpload = () => {
    //     if (!formData.file) {
    //         setErrors((prev) => ({
    //             ...prev,
    //             file: 'FileRequired',
    //         }))
    //         return
    //     }

    //     if (onValidate) {
    //         onValidate(formData.file)
    //     }
    // }

    const handleUpload = async () => {
        if (!validateForm()) {
            return
        }

        const formDataToUpload = new FormData()

        // required file field name: "file"
        formDataToUpload.append('file', formData.file, formData.file.name) // filename is important

        // metadata fields
        formDataToUpload.append('customFileName', formData.name) // or your own name
        formDataToUpload.append(
            'CSVInboxTypeID',
            String(formData?.inboxType?.csvInboxTypeID),
        )
        formDataToUpload.append('TicketNumber', formData.ticketNo)
        if (formData?.category?.csvInboxCategoryID != null) {
            formDataToUpload.append(
                'CSVInboxCategoryID',
                String(formData.category.csvInboxCategoryID),
            )
        }
        formDataToUpload.append('Comment', formData.comments)

        // formDataToUpload.append(
        //     'ResourceSystemGUID',
        //     formData?.resourceSystem?.resourceSystemGUID,
        // )
        try {
            if (onUpload) {
                const response = await onUpload(formDataToUpload)
                const result = response?.data

                if (result?.isSuccess) {
                    showSuccessMessage(result?.message || t('UploadSuccessful'))
                    handleClose()
                } else {
                    // Store error messages in state to display in dialog
                    if (result?.errorMessages?.length > 0) {
                        setApiErrors(result.errorMessages)
                    } else if (result?.message) {
                        setApiErrors([result.message])
                    } else {
                        setApiErrors([t('UploadFailed')])
                    }
                }
            }
        } catch (error) {
            console.error('Upload failed:', error)
            setApiErrors([t('UploadFailed')])
        } finally {
        }
    }

    const handleClose = () => {
        setOpenModal(false)
        setFormData(initialFormState)
        setErrors(initialErrorState)
        setApiErrors([])
    }

    const handleOpen = () => {
        setOpenModal(true)
    }

    return (
        <>
            <GenericButton
                onClick={handleOpen}
                sx={{
                    borderRadius: '4px',
                    padding: '0px 20px',
                    backgroundColor: theme.palette.primary.main,
                    border: 'solid 1px #01ae8f',
                    color: '#ffffff',
                    fontSize: '14px',
                    height: '40px',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                    },
                }}
                startIcon={
                    <Icon
                        name={'Plus'}
                        fill="white"
                        style={{
                            width: '1.6rem',
                            height: '1.6rem',
                        }}
                    />
                }
            >
                {t(btnLabelKey)}
            </GenericButton>

            <CustomDialog
                open={openModal}
                onClose={handleClose}
                title={t('')}
                sx={{
                    '& .MuiDialog-paper': {
                        minWidth: '800px',
                    },
                }}
            >
                <Box
                    sx={{
                        '& h2': {
                            fontSize: '18px',
                            color: '#1b1f1f',
                            fontWeight: '600',
                        },
                    }}
                >
                    {/* Header */}
                    <Box sx={{ padding: '32px 32px' }}>
                        <CloseIconeBox onClick={handleClose}>
                            <Icon
                                name="Close"
                                width="16px"
                                height="16px"
                                color="#959598"
                            />
                        </CloseIconeBox>
                        <Typography variant="h2">
                            {t(headerLabelKey)}
                        </Typography>
                    </Box>

                    {/* Form Fields */}
                    <Box>
                        <Box
                            sx={{
                                // padding: '20px 32px',
                                backgroundColor: '#fbfbfd !important',
                            }}
                        >
                            {/* Upload File Field */}
                            <ItemWrapper
                                title={t('UploadFile')}
                                isRequired={true}
                                tooltipMessage={t('UploadFileTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <FileUpload
                                    value={formData.file}
                                    onChange={(file) =>
                                        handleFieldChange('file', file)
                                    }
                                    accept=".csv"
                                    placeholder={t('NoFileChosen')}
                                    showUploadButton={false}
                                    disabled={isLoading}
                                />
                                {errors.file && (
                                    <Box paddingLeft={'.2rem'}>
                                        <RequiredFieldErrorMessage
                                            errorMsgKey={errors.file}
                                        />
                                    </Box>
                                )}
                            </ItemWrapper>
                            {/* Name Field */}
                            <ItemWrapper
                                title={t('Name')}
                                isRequired={true}
                                tooltipMessage={t('NameTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <TextInput
                                        disabled={isLoading}
                                        value={formData.name}
                                        handleChange={(val) => {
                                            handleFieldChange('name', val)
                                        }}
                                        placeholder={t('EnterName')}
                                    />
                                </Box>
                                {errors.name && (
                                    <Box paddingLeft={'.2rem'}>
                                        <RequiredFieldErrorMessage
                                            errorMsgKey={errors.name}
                                        />
                                    </Box>
                                )}
                            </ItemWrapper>

                            {/* Inbox Type Field */}
                            <ItemWrapper
                                title={t('InboxType')}
                                isRequired={true}
                                tooltipMessage={t('InboxTypeTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Select
                                        disabled={isLoading}
                                        options={
                                            inboxTypesData?.map((item) => {
                                                return {
                                                    id: item.csvInboxTypeID,
                                                    ...item,
                                                }
                                            }) || []
                                        }
                                        onChange={(data) => {
                                            handleFieldChange('inboxType', data)
                                        }}
                                        value={formData.inboxType}
                                        getOptionLabel={(option) =>
                                            option?.name ||
                                            option?.friendlyName ||
                                            ''
                                        }
                                        placeholder={t('SelectInboxType')}
                                    />
                                </Box>
                                {errors.inboxType && (
                                    <Box paddingLeft={'.2rem'}>
                                        <RequiredFieldErrorMessage
                                            errorMsgKey={errors.inboxType}
                                        />
                                    </Box>
                                )}
                            </ItemWrapper>
                            {/* <ItemWrapper
                                title={t('ResourceSystem')}
                                isRequired={true}
                                tooltipMessage={t(
                                    'ResourceSystemTooltipMessage',
                                )}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <EidAutocomplete.New
                                        disabled={isLoading}
                                        // label={t('InboxType')}
                                        placeholder={t('SelectResourceSystem')}
                                        url={'api/CSVInbox/resourcesystems'}
                                        queryParams={{}}
                                        method="GET"
                                        dedupingInterval={60000}
                                        value={
                                            formData.resourceSystem
                                                ? formData.resourceSystem
                                                : null
                                        }
                                        getOptionLabel={(option) =>
                                            option?.accountStoreFriendlyName ||
                                            option?.friendlyName ||
                                            ''
                                        }
                                        onChange={(e, value) => {
                                            handleFieldChange(
                                                'resourceSystem',
                                                value,
                                            )
                                        }}
                                    />
                                </Box>
                                {errors.resourceSystem && (
                                    <Box paddingLeft={'.2rem'}>
                                        <RequiredFieldErrorMessage
                                            errorMsgKey={errors.resourceSystem}
                                        />
                                    </Box>
                                )}
                            </ItemWrapper> */}
                            <ItemWrapper
                                title={t('Category')}
                                isRequired={false}
                                tooltipMessage={t('CategoryTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Select
                                        disabled={isLoading}
                                        nullableText={t('None')}
                                        options={
                                            categoriesData?.map((item) => {
                                                return {
                                                    id: item.csvInboxCategoryID,
                                                    ...item,
                                                }
                                            }) || []
                                        }
                                        onChange={(data) => {
                                            handleFieldChange('category', data)
                                        }}
                                        value={formData.category}
                                        getOptionLabel={(option) =>
                                            option?.name ||
                                            option?.friendlyName ||
                                            ''
                                        }
                                        placeholder={t('SelectCategory')}
                                    />
                                </Box>
                                {errors.category && (
                                    <Box paddingLeft={'.2rem'}>
                                        <RequiredFieldErrorMessage
                                            errorMsgKey={errors.category}
                                        />
                                    </Box>
                                )}
                            </ItemWrapper>

                            {/* Ticket No Field */}
                            <ItemWrapper
                                title={t('TicketNo')}
                                isRequired={false}
                                tooltipMessage={t('TicketNoTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <TextInput
                                        disabled={isLoading}
                                        value={formData.ticketNo}
                                        handleChange={(val) => {
                                            handleFieldChange('ticketNo', val)
                                        }}
                                        placeholder={t('EnterTicketNo')}
                                    />
                                </Box>
                            </ItemWrapper>

                            {/* Comments Field */}
                            <ItemWrapper
                                title={t('Comments')}
                                isRequired={false}
                                tooltipMessage={t('CommentsTooltipMessage')}
                                childStyle={{ flex: 1 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <TextInput
                                        disabled={isLoading}
                                        value={formData.comments}
                                        handleChange={(val) =>
                                            handleFieldChange('comments', val)
                                        }
                                        variant="outlined"
                                        placeholder={t('EnterComments')}
                                        multiline
                                        rows={3}
                                    />
                                </Box>
                            </ItemWrapper>

                            {/* API Error Messages */}
                            {apiErrors.length > 0 && (
                                <Box sx={{ margin: '16px 32px' }}>
                                    <InfoBoxWithIcon
                                        message={`<strong>${t(
                                            'UploadErrors',
                                        )}:</strong><ul style="margin: 8px 0 0 0; padding-left: 20px;">${apiErrors
                                            ?.map(
                                                (error) => `<li>${error}</li>`,
                                            )
                                            .join('')}</ul>`}
                                        iconName="VeryCriticalRisk"
                                        iconAreaStyles={{
                                            background:
                                                'linear-gradient(135deg,rgb(209, 98, 73) 0%,rgb(216, 48, 39) 100%)',
                                        }}
                                        rootStyles={{
                                            width: '100%',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Footer Buttons */}
                    <Box
                        sx={{
                            padding: '24px 32px 24px 32px',
                            boxShadow: '0 3px 7px 4px rgba(0, 0, 0, 0.08)',
                            '& button': {
                                border: `solid 1px ${theme.palette.primary.main}`,
                                padding: '9px 24px',
                            },
                        }}
                    >
                        <Button
                            onClick={handleUpload}
                            loading={isLoading}
                            sx={{
                                background: theme.palette.primary.main,
                                color: '#fff',
                                maxWidth: '240px',
                                '& svg': {
                                    marginRight: '15px',
                                },
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <Fragment>
                                <Icon
                                    name="Save"
                                    width="16px"
                                    height="16px"
                                    color="#fff"
                                />
                                {t('Upload')}
                            </Fragment>
                        </Button>
                        <Button
                            sx={{
                                color: theme.palette.primary.main,
                                marginLeft: '30px',
                                maxWidth: '90px',
                            }}
                            onClick={handleClose}
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                </Box>
            </CustomDialog>
        </>
    )
}

export default MassUpload
